import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { useAuthStore } from '../lib/auth'
import { supabase } from '../lib/supabase'

export default function ResetPassword() {
  const navigate = useNavigate()
  const resetPassword = useAuthStore((s) => s.resetPassword)

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [hasSession, setHasSession] = useState(false)

  useEffect(() => {
    // 1. Listen for Supabase auth state changes (including PASSWORD_RECOVERY event)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'PASSWORD_RECOVERY' || session) {
        setHasSession(true)
        setError('')
      }
    })

    // 2. Check current session or URL parameters (PKCE code or Hash token)
    const checkSession = async () => {
      const urlParams = new URLSearchParams(window.location.search)
      const code = urlParams.get('code')

      if (code) {
        // Handle PKCE recovery flow
        const { data, error: exchangeErr } = await supabase.auth.exchangeCodeForSession(code)
        if (data?.session) {
          setHasSession(true)
          return
        } else if (exchangeErr) {
          setError(exchangeErr.message || 'Invalid or expired reset link.')
          return
        }
      }

      const { data: { session } } = await supabase.auth.getSession()
      if (session) {
        setHasSession(true)
      } else {
        const hashParams = new URLSearchParams(window.location.hash.substring(1))
        const type = hashParams.get('type')
        if (type === 'recovery') {
          setTimeout(async () => {
            const { data: { session: s } } = await supabase.auth.getSession()
            if (s) {
              setHasSession(true)
            } else {
              setError('Invalid or expired reset link. Please request a new password reset link.')
            }
          }, 1200)
        } else {
          // Allow input attempt if hash contains access_token
          if (window.location.hash.includes('access_token')) {
            setHasSession(true)
          } else {
            setError('Invalid or missing reset token. Please request a new password reset link.')
          }
        }
      }
    }

    checkSession()

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    setLoading(true)

    try {
      // Execute resetPassword with a 10-second timeout guarantee
      const resetPromise = resetPassword(formData.password)
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Reset request timed out. Please ensure your reset link is valid or request a new one.')), 10000)
      )

      await Promise.race([resetPromise, timeoutPromise])

      setSuccess(true)
      setLoading(false)

      setTimeout(() => {
        navigate('/login')
      }, 2000)
    } catch (err) {
      setError(err.message || 'Failed to reset password. Token may be expired.')
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#0a0e17] text-white">
      <Header />
      <div className="flex items-center justify-center px-4 py-12">
        <div className="card max-w-md w-full">
          {!success ? (
            <>
              <h2 className="text-3xl font-bold mb-2 text-center" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                Set New Password
              </h2>
              <p className="text-gray-400 text-center mb-6">
                Choose a strong password for your account
              </p>

              {error && (
                <div className="bg-red-500/20 text-red-400 p-3 rounded-lg mb-4 text-center border border-red-500/30">
                  {error}
                </div>
              )}

              {hasSession ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-gray-400 mb-2 text-sm">New Password</label>
                    <input
                      type="password"
                      className="w-full bg-gray-900/50 border border-white/20 rounded-lg p-3 focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength={8}
                      placeholder="At least 8 characters"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-400 mb-2 text-sm">Confirm Password</label>
                    <input
                      type="password"
                      className="w-full bg-gray-900/50 border border-white/20 rounded-lg p-3 focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      required
                      placeholder="Re-enter password"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </button>
                </form>
              ) : !error ? (
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-400">Verifying reset link...</p>
                </div>
              ) : (
                <div className="text-center">
                  <Link to="/forgot-password" className="text-cyan-400 hover:text-cyan-300 transition">
                    Request a new reset link
                  </Link>
                </div>
              )}
            </>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold mb-2">Password Reset!</h2>
              <p className="text-gray-400 mb-4">
                Your password has been successfully reset.
              </p>
              <p className="text-gray-500 text-sm">
                Redirecting to login...
              </p>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  )
}
